// TennisTrack Hub — Pagina Premium / Upgrade | 029 Project
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Premium.css';

const FEATURES_FREE = [
  'Gestione atleti illimitata',
  'Lezioni e pacchetti',
  'Allenamenti tennis & fitness',
  'Video YouTube illimitati',
  'Fino a 4 template personali',
  'Libreria template base',
];

const FEATURES_PREMIUM = [
  'Template illimitati',
  'Template Premium esclusivi della community',
  'Pubblica i tuoi template nella libreria',
  'Commenta i template degli altri coach',
  'Esporta allenamenti in PDF',
  'Esporta template in PDF',
  'Supporto prioritario 029 Project',
];

const FAQ = [
  {
    q: 'Posso cancellare in qualsiasi momento?',
    a: 'Sì. Puoi cancellare il tuo abbonamento Premium quando vuoi. Rimarrai Premium fino alla fine del periodo già pagato.'
  },
  {
    q: 'Il pagamento è sicuro?',
    a: 'Tutti i pagamenti sono gestiti da Stripe, leader mondiale nei pagamenti online. I tuoi dati della carta non passano mai dai nostri server.'
  },
  {
    q: 'Cosa succede ai miei template se torno al piano Free?',
    a: 'I template già creati rimangono nella tua libreria. Non puoi crearne di nuovi oltre il limite di 4, ma quelli esistenti restano accessibili.'
  },
  {
    q: 'Posso avere la fattura?',
    a: 'Sì. Stripe invia automaticamente una ricevuta via email dopo ogni pagamento. Puoi anche scaricare le fatture dal portale clienti Stripe.'
  },
];

const PaginaPremium = () => {
  const navigate = useNavigate();
  const { utente, login } = useAuth();
  const [searchParams] = useSearchParams();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [abbonamento, setAbbonamento] = useState(null);
  const [cancelloLoading, setCancelloLoading] = useState(false);
  const [successo, setSuccesso] = useState(false);
  const [verificaLoading, setVerificaLoading] = useState(false);

  // Successo redirect da Stripe
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && utente) {
      verificaPagamento(sessionId);
    }
  }, [sessionId, utente]);

  useEffect(() => {
    caricaAbbonamento();
  }, []);

  const caricaAbbonamento = async () => {
    try {
      const res = await api.get('/stripe/abbonamento');
      setAbbonamento(res.data.abbonamento);
    } catch {
      // silenzioso — Stripe potrebbe non essere configurato
    }
  };

  const verificaPagamento = async (sid) => {
    setVerificaLoading(true);
    try {
      await api.get(`/stripe/successo?session_id=${sid}`);
      setSuccesso(true);
      // Ricarica utente per aggiornare piano
      const resMe = await api.get('/auth/me');
      // Aggiorna context (se disponibile)
      if (resMe.data?.utente) {
        localStorage.setItem('tt_token', localStorage.getItem('tt_token')); // trigger refresh
      }
    } catch (err) {
      setErrore(err.response?.data?.error || 'Impossibile verificare il pagamento.');
    } finally {
      setVerificaLoading(false);
    }
  };

  const avviaCheckout = async () => {
    setErrore('');
    setCheckoutLoading(true);
    try {
      const res = await api.post('/stripe/checkout');
      // Redirect a Stripe Checkout
      window.location.href = res.data.sessionUrl;
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Errore nell\'avvio del pagamento.';
      const dettaglio = err.response?.data?.dettaglio;
      setErrore(dettaglio ? `${errMsg} (${dettaglio})` : errMsg);
      setCheckoutLoading(false);
    }
  };

  const cancellaAbbonamento = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare il tuo abbonamento Premium? Rimarrai Premium fino alla fine del periodo già pagato.')) return;
    setCancelloLoading(true);
    setErrore('');
    try {
      const res = await api.post('/stripe/cancella');
      alert(res.data.messaggio);
      await caricaAbbonamento();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nella cancellazione.');
    } finally {
      setCancelloLoading(false);
    }
  };

  const isPremium = utente?.piano === 'premium';

  // ── PAGINA SUCCESSO ──
  if (successo || (sessionId && !verificaLoading && !errore)) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main">
          <div className="premium-successo">
            <span className="successo-icon">🎉</span>
            <h1>Benvenuto nel Piano Premium!</h1>
            <p>Il tuo upgrade è andato a buon fine. Ora hai accesso a tutte le funzionalità Premium di TennisTrack Hub.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/template')}>
                📋 Esplora template Premium
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
                Dashboard →
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (verificaLoading) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main">
          <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-secondary)' }}>
            ⏳ Verifica del pagamento in corso...
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Hero */}
        <div className="premium-hero">
          <span className="ph-icon">⭐</span>
          <h1>TennisTrack Premium</h1>
          <p>Porta il tuo coaching al livello successivo. PDF, template illimitati, community e molto altro.</p>
        </div>

        {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

        {/* Abbonamento attivo */}
        {isPremium && abbonamento && (
          <div className="abbonamento-attivo">
            <div className="abb-info">
              <h3>⭐ Piano Premium attivo</h3>
              <p>
                {abbonamento.stato === 'annullato'
                  ? 'Cancellazione programmata — attivo fino alla scadenza del periodo'
                  : `Abbonamento mensile · €${(abbonamento.importo_cents / 100).toFixed(2)}/mese`}
              </p>
            </div>
            {abbonamento.stato !== 'annullato' && abbonamento.stripe_subscription_id && (
              <button
                className="btn-cancella-abb"
                onClick={cancellaAbbonamento}
                disabled={cancelloLoading}
              >
                {cancelloLoading ? 'Attendere...' : 'Cancella abbonamento'}
              </button>
            )}
          </div>
        )}

        {/* Piani a confronto */}
        <div className="piani-grid">

          {/* Free */}
          <div className={`piano-card ${!isPremium ? 'piano-attuale' : ''}`}>
            {!isPremium && <span className="piano-badge-current">Piano attuale</span>}
            <h2>🎾 Free</h2>
            <div className="piano-prezzo">
              <span className="prezzo-free">€0</span>
              <span className="prezzo-periodo">/sempre</span>
            </div>
            <ul className="piano-features">
              {FEATURES_FREE.map((f, i) => (
                <li key={i}><span className="feature-check">✓</span>{f}</li>
              ))}
            </ul>
            {!isPremium
              ? <span className="piano-attuale-label">✓ Piano attuale</span>
              : <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center' }}>Piano di partenza</span>
            }
          </div>

          {/* Premium */}
          <div className={`piano-card piano-premium ${isPremium ? 'piano-attuale' : ''}`}>
            {isPremium && <span className="piano-badge-current">Piano attuale</span>}
            <h2>⭐ Premium</h2>
            <div className="piano-prezzo">
              <span className="prezzo-valore">€9,99</span>
              <span className="prezzo-periodo">/mese</span>
            </div>
            <ul className="piano-features">
              {FEATURES_PREMIUM.map((f, i) => (
                <li key={i}><span className="feature-check">✓</span>{f}</li>
              ))}
            </ul>
            {isPremium ? (
              <span className="piano-attuale-label">⭐ Sei già Premium!</span>
            ) : (
              <button
                className="btn-premium-checkout"
                onClick={avviaCheckout}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? '⏳ Avvio pagamento...' : '⭐ Passa a Premium'}
              </button>
            )}
          </div>
        </div>

        {/* Stripe badge */}
        {!isPremium && (
          <div className="stripe-badge">
            🔒 Pagamento sicuro con Stripe · Cancella quando vuoi
          </div>
        )}

        {/* FAQ */}
        <div className="card premium-faq" style={{ marginTop: '32px' }}>
          <h3>❓ Domande frequenti</h3>
          {FAQ.map((faq, idx) => (
            <div key={idx} className="faq-item">
              <p className="faq-q">{faq.q}</p>
              <p className="faq-a">{faq.a}</p>
            </div>
          ))}
        </div>

        {/* Footer branding */}
        <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '16px' }}>
          <img src="/img/madeby029.png" alt="029 Project" style={{ height: '32px', opacity: 0.6 }} />
        </div>

      </main>
    </div>
  );
};

export default PaginaPremium;
